import { useState, type FormEvent } from 'react';
import { PasswordField } from './password-field';
import type { SignInResult, NewPasswordChallenge } from '../../components/shared/auth/cognito';

interface LoginFormProps {
  onSignIn: (email: string, password: string) => Promise<SignInResult>;
  onNewPasswordRequired: (challenge: NewPasswordChallenge) => void;
  onForgotPassword: () => void;
  /** One-shot confirmation to show above the form (e.g. after a password reset). */
  notice?: string | null;
}

export function LoginForm({
  onSignIn,
  onNewPasswordRequired,
  onForgotPassword,
  notice,
}: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await onSignIn(email.trim(), password);
      if (result.kind === 'newPasswordRequired') {
        // First login: hand the challenge up so the parent shows the reset card.
        // Leave submitting true — this card is about to unmount.
        onNewPasswordRequired(result.challenge);
        return;
      }
      // kind 'ok' → useAuth flips to signed-in and this card unmounts.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm">
      <div className="terminal-card overflow-hidden">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="win-dots relative block h-2.5 w-2.5" aria-hidden />
          <span className="ml-8 font-mono text-xs text-muted">admin — sign in</span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6" noValidate>
          {notice && (
            <p role="status" className="font-mono text-sm text-accent">
              {notice}
            </p>
          )}

          <div>
            <label htmlFor="admin-email" className="font-mono text-sm text-muted">
              email
            </label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-fg outline-none focus:border-accent"
            />
          </div>

          <PasswordField
            id="admin-password"
            label="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />

          {error && (
            <p role="alert" className="font-mono text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-accent px-4 py-2 font-mono text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'signing in…' : 'sign in'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={onForgotPassword}
              className="font-mono text-xs text-muted hover:text-accent"
            >
              forgot password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
